import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { useAuth } from '../context/AuthContext';
import { useRTL } from '../context/RTLContext';
import { CardBox, Button } from '../components/UI';
import {
  IconShield,
  IconSettings,
  IconMail,
  IconPhone,
  IconLanguage,
  IconPlus,
  IconTrash,
  IconExternalLink,
  IconAlertCircle,
  IconCircleCheck,
  IconLogout
} from '@tabler/icons-react';

const AccountSettings = () => {
  const { t } = useTranslation();
  const { user, updateUser, logout } = useAuth();
  useRTL(); // ensure RTL provider stays initialised on this page
  const [activeTab, setActiveTab] = useState('preferences');

  // Inline status banner — replaces all the alert() calls.
  const [status, setStatus] = useState(null); // { type: 'success'|'error', message }
  useEffect(() => {
    if (!status) return undefined;
    const tm = setTimeout(() => setStatus(null), 4500);
    return () => clearTimeout(tm);
  }, [status]);

  // Profile state
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    language: user?.lockedLanguage || i18n.language || 'en'
  });

  // Business info state
  const [businessInfo, setBusinessInfo] = useState({ name: '', website: '' });
  // eslint-disable-next-line no-unused-vars
  const [loadingBusiness, setLoadingBusiness] = useState(true);

  // Competitors state
  const [competitors, setCompetitors] = useState([]);
  const [loadingCompetitors, setLoadingCompetitors] = useState(true);
  const [showAddCompetitor, setShowAddCompetitor] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState({ name: '', domain: '' });
  const [competitorToDelete, setCompetitorToDelete] = useState(null);

  const tabs = [
    { id: 'preferences', label: t('settings.tabs.preferences'), icon: IconSettings },
    { id: 'account', label: t('settings.tabs.account'), icon: IconShield }
  ];

  // Fetch business info
  useEffect(() => {
    fetchBusinessInfo();
  }, []);

  // Fetch competitors
  useEffect(() => {
    if (activeTab === 'preferences') {
      fetchCompetitors();
    }
  }, [activeTab]);

  const fetchBusinessInfo = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/users/profile`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBusinessInfo({
          name: data.business_name || 'Not set',
          website: data.business_website || 'Not set'
        });
      }
    } catch (error) {
      console.error('Failed to fetch business info:', error);
    } finally {
      setLoadingBusiness(false);
    }
  };

  const fetchCompetitors = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/users/competitors`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompetitors(data.data.competitors || []);
      }
    } catch (error) {
      console.error('Failed to fetch competitors:', error);
    } finally {
      setLoadingCompetitors(false);
    }
  };

  const handleAddCompetitor = async () => {
    if (!newCompetitor.name.trim()) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/users/competitors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          competitor_name: newCompetitor.name,
          competitor_domain: newCompetitor.domain || null
        })
      });

      if (response.ok) {
        await fetchCompetitors();
        setShowAddCompetitor(false);
        setNewCompetitor({ name: '', domain: '' });
        setStatus({ type: 'success', message: t('settings.competitors.added', 'Competitor added.') });
      } else {
        const err = await response.json().catch(() => ({}));
        setStatus({ type: 'error', message: err.error || t('settings.competitors.addFailed', 'Failed to add competitor.') });
      }
    } catch (error) {
      console.error('Failed to add competitor:', error);
      setStatus({ type: 'error', message: t('settings.competitors.addFailed', 'Failed to add competitor.') });
    }
  };

  const handleDeleteCompetitor = async (id) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/users/competitors/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      });

      if (response.ok) {
        await fetchCompetitors();
        setCompetitorToDelete(null);
        setStatus({ type: 'success', message: t('settings.competitors.removed', 'Competitor removed.') });
      } else {
        setStatus({ type: 'error', message: t('settings.competitors.removeFailed', 'Failed to remove competitor.') });
      }
    } catch (error) {
      console.error('Failed to delete competitor:', error);
      setStatus({ type: 'error', message: t('settings.competitors.removeFailed', 'Failed to remove competitor.') });
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          phone: profileData.phone,
        })
      });

      if (response.ok) {
        // Push updates into AuthContext so the sidebar header + greeting refresh
        updateUser({
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          phone: profileData.phone,
        });
        // Apply language change live (unless server-locked)
        if (!user?.lockedLanguage && profileData.language && profileData.language !== i18n.language) {
          await i18n.changeLanguage(profileData.language);
          document.documentElement.dir = profileData.language === 'he' ? 'rtl' : 'ltr';
          document.documentElement.lang = profileData.language;
          localStorage.setItem('eyehill_language', profileData.language);
        }
        setStatus({ type: 'success', message: t('settings.account.saved', 'Profile updated.') });
      } else {
        const err = await response.json().catch(() => ({}));
        setStatus({ type: 'error', message: err.error || t('settings.account.saveFailed', 'Failed to update profile.') });
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      setStatus({ type: 'error', message: t('settings.account.saveFailed', 'Failed to update profile.') });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-gray-100">{t('settings.title')}</h1>

      {/* Inline status banner — replaces all alert() calls. Auto-dismisses. */}
      {status && (
        <div
          role="status"
          aria-live="polite"
          className={`mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${
            status.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-100'
              : 'bg-red-50 border-red-200 text-red-900 dark:bg-red-900/30 dark:border-red-800 dark:text-red-100'
          }`}
        >
          {status.type === 'success'
            ? <IconCircleCheck size={18} className="mt-0.5 flex-shrink-0" />
            : <IconAlertCircle size={18} className="mt-0.5 flex-shrink-0" />}
          <span className="flex-1">{status.message}</span>
          <button onClick={() => setStatus(null)} className="text-current/60 hover:text-current">×</button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-gray-100 dark:bg-[#262626] rounded-lg p-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all flex-1
                ${activeTab === tab.id
                  ? 'bg-white dark:bg-[#1f1f1f] text-gray-900 dark:text-[#f5f5f5] shadow-sm'
                  : 'text-gray-600 dark:text-[#c4c4c4] hover:text-gray-900 dark:hover:text-[#f5f5f5]'
                }
              `}
            >
              <Icon size={18} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <div className="space-y-6">
          {/* Tracked Competitors */}
          <CardBox>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold">{t('settings.competitors.title')}</h2>
                  <p className="text-sm text-gray-600 dark:text-[#c4c4c4] mt-1">{t('settings.competitors.description')}</p>
                </div>
                <Button
                  onClick={() => setShowAddCompetitor(!showAddCompetitor)}
                  leftIcon={<IconPlus size={18} />}
                >
                  {t('settings.competitors.add')}
                </Button>
              </div>

              {/* Add Competitor Form — wrapped in <form> so Enter submits. */}
              {showAddCompetitor && (
                <form
                  onSubmit={(e) => { e.preventDefault(); handleAddCompetitor(); }}
                  className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      autoFocus
                      placeholder={t('settings.competitors.namePlaceholder')}
                      value={newCompetitor.name}
                      onChange={(e) => setNewCompetitor({ ...newCompetitor, name: e.target.value })}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d8f9b8]"
                    />
                    <input
                      type="text"
                      placeholder={t('settings.competitors.domainPlaceholder')}
                      value={newCompetitor.domain}
                      onChange={(e) => setNewCompetitor({ ...newCompetitor, domain: e.target.value })}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d8f9b8]"
                    />
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => setShowAddCompetitor(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={!newCompetitor.name.trim()}
                      className="px-4 py-2 bg-[#d8f9b8] text-[#1a1a1a] font-medium rounded-lg hover:bg-[#c8e9a8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {t('common.add')}
                    </button>
                  </div>
                </form>
              )}

              {/* Competitors List */}
              {loadingCompetitors ? (
                <div className="text-center py-8 text-gray-500 dark:text-[#999999]">
                  {t('common.loading')}
                </div>
              ) : competitors.length === 0 ? (
                <div className="text-center py-8">
                  <IconAlertCircle size={48} className="mx-auto text-gray-400 dark:text-[#999999] mb-3" />
                  <p className="text-gray-600 dark:text-[#c4c4c4]">{t('settings.competitors.noCompetitors')}</p>
                  <p className="text-sm text-gray-500 dark:text-[#999999] mt-1">{t('settings.competitors.addOrDiscover')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <tbody className="divide-y divide-gray-100 dark:divide-[#2e2e2e]">
                      {competitors.map((competitor) => (
                        <tr key={competitor.id} className="hover:bg-gray-50 dark:hover:bg-[#262626]">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-[#f5f5f5]">{competitor.competitor_name}</td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-[#c4c4c4]">
                            {competitor.competitor_domain ? (
                              <a href={`https://${competitor.competitor_domain}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-700">
                                {competitor.competitor_domain}
                                <IconExternalLink size={14} />
                              </a>
                            ) : (
                              <span className="text-gray-400 dark:text-[#999999]">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => setCompetitorToDelete(competitor)}
                              className="text-red-600 dark:text-red-300 hover:text-red-700 dark:hover:text-red-200 p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                              title={t('common.delete')}
                            >
                              <IconTrash size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </CardBox>

          {/* Business Information */}
          <CardBox>
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4">{t('settings.business.title')}</h2>
              <p className="text-sm text-gray-600 dark:text-[#c4c4c4] mb-6">{t('settings.business.description')}</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-1">
                    {t('settings.business.name')}
                  </label>
                  <div className="px-4 py-3 bg-gray-50 dark:bg-[#262626] rounded-lg border border-gray-200 dark:border-[#2e2e2e]">
                    <p className="text-gray-900 dark:text-[#f5f5f5]">{businessInfo.name}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-1">
                    {t('settings.business.website')}
                  </label>
                  <div className="px-4 py-3 bg-gray-50 dark:bg-[#262626] rounded-lg border border-gray-200 dark:border-[#2e2e2e]">
                    <p className="text-gray-900 dark:text-[#f5f5f5]">{businessInfo.website}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 flex gap-3">
                <IconAlertCircle size={20} className="text-blue-600 flex-shrink-0" />
                <p className="text-sm text-blue-800">
                  {t('settings.business.contactSupport')}
                </p>
              </div>
            </div>
          </CardBox>

          {/* Account Information */}
          <CardBox>
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-6">{t('settings.account.title')}</h2>

              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-1">
                      {t('settings.account.firstName')}
                    </label>
                    <input
                      type="text"
                      value={profileData.firstName}
                      onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-[#2e2e2e] dark:bg-[#1f1f1f] dark:text-[#f5f5f5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d8f9b8]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-1">
                      {t('settings.account.lastName')}
                    </label>
                    <input
                      type="text"
                      value={profileData.lastName}
                      onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-[#2e2e2e] dark:bg-[#1f1f1f] dark:text-[#f5f5f5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d8f9b8]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-1">
                      <div className="flex items-center gap-2">
                        <IconMail size={16} />
                        {t('settings.account.email')}
                      </div>
                    </label>
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-[#2e2e2e] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d8f9b8] bg-gray-50 dark:bg-[#262626] dark:text-[#c4c4c4]"
                      disabled
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-1">
                      <div className="flex items-center gap-2">
                        <IconPhone size={16} />
                        {t('settings.account.phone')}
                      </div>
                    </label>
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-[#2e2e2e] dark:bg-[#1f1f1f] dark:text-[#f5f5f5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d8f9b8]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-[#c4c4c4] mb-1">
                    <div className="flex items-center gap-2">
                      <IconLanguage size={16} />
                      {t('settings.account.language')}
                    </div>
                  </label>
                  <select
                    value={profileData.language}
                    onChange={(e) => setProfileData({...profileData, language: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-[#2e2e2e] dark:bg-[#1f1f1f] dark:text-[#f5f5f5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d8f9b8]"
                  >
                    <option value="en">English</option>
                    <option value="he">עברית</option>
                  </select>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-[#d8f9b8] text-gray-900 font-medium rounded-lg hover:bg-[#c8e9a8] transition-colors"
                  >
                    {t('common.save')}
                  </button>
                </div>
              </form>
            </div>
          </CardBox>
        </div>
      )}

      {/* Account Tab — sign out only. Notifications, password change,
          two-factor and "active sessions" panels were stubs with no backend
          (handlers were console.log TODOs, sessions were hardcoded fake
          "Chrome on MacOS"). They'll come back when the API exists. */}
      {activeTab === 'account' && (
        <div className="space-y-6">
          <CardBox>
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-medium flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    <IconLogout size={18} />
                    {t('settings.account.signOut', 'Sign Out')}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('settings.account.signOutDesc', 'Sign out of your account on this device')}
                  </p>
                </div>
                <button
                  onClick={logout}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t('settings.account.signOutButton', 'Sign Out')}
                </button>
              </div>
            </div>
          </CardBox>
        </div>
      )}

      {/* Delete Competitor Confirmation Dialog */}
      {competitorToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-[#1f1f1f] rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-2">{t('settings.competitors.deleteTitle')}</h3>
            <p className="text-gray-600 dark:text-[#c4c4c4] mb-6">
              {t('settings.competitors.deleteConfirm')} <strong>{competitorToDelete.competitor_name}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setCompetitorToDelete(null)}
                className="px-4 py-2 text-gray-700 dark:text-[#c4c4c4] hover:bg-gray-100 dark:hover:bg-[#262626] rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleDeleteCompetitor(competitorToDelete.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
